import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useStyles } from "./OverallDowntimeSummary.styles";
import { useTranslation } from "react-i18next";
import { OverallDowntimeSummaryPieChart } from "../OverallDowntimeSummaryPieChart/OverallDowntimeSummaryPieChart.view";
import { OverallDowntimeSummaryPieChartCenteredMetric } from "components/OverallDowntimeSummaryPieChartCenteredMetric/OverallDowntimeSummaryPieChartCenteredMetric.view";
import { OverallDowntimeSummaryPieLegends } from "../OverallDowntimeSummaryPieLegends/OverallDowntimeSummaryPieLegends.view";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { IEventFailureCodeSummaryPerMC } from "entities/Mills/MillsEntityInterfaces";
import { calculateGraphTwoDigitPercentage, truncateLongText } from "utilities/utilities";
import { IPieChartData, Stat } from "./OverallDowntimeSummaryInterfaces";
import NoData from "components/NoData/NoData.view";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReport/components/SummaryReportFilters/useMillFilters";

export const OverallDowntimeSummary = () => {
    const [highlight, setHighlight] = useState({ name: "", value: 0 });
    const classes = useStyles();
    const { t } = useTranslation();
    const { millId } = useParams();
    const { filters } = useContext(SummaryReportDashboardContext);
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
            displayIn: filters.displayIn ? filters.displayIn : initialMillFilters.displayIn,
        }),
        [filters],
    );
    const { eventReportsFailureDetailsStatsByMill } = MillsEntity.GetEventReportsFailureTypesStatsByMill(
        +millId!,
        queryFilters,
    );

    const [noInitialDataInMinutes, setNoInitialDataInMinutes] = useState<boolean>(false);

    const statsObjectMemoized: { [key: string]: Stat } = useMemo(() => {
        const statsObject: { [key: string]: Stat } = {};
        eventReportsFailureDetailsStatsByMill.forEach((stat: IEventFailureCodeSummaryPerMC) => {
            if (Object.keys(statsObject).includes(stat.failure_type.name)) {
                statsObject[stat.failure_type.name].total_minutes =
                    statsObject[stat.failure_type.name].total_minutes + stat.seconds;
                statsObject[stat.failure_type.name].total_occurrences =
                    statsObject[stat.failure_type.name].total_occurrences + stat.occurrences;
            } else {
                const values: Stat = {
                    name: stat.failure_type.name,
                    color_code: stat.failure_type.color_code,
                    total_minutes: stat.seconds,
                    total_occurrences: stat.occurrences,
                };

                statsObject[values.name] = values;
            }
        });

        Object.values(statsObject).forEach(
            (stat) =>
                (stat.total_minutes =
                    Math.round(stat.total_minutes / 60) < 1 ? 1 : Math.round(stat.total_minutes / 60)),
        );

        return statsObject;
    }, [eventReportsFailureDetailsStatsByMill]);

    const aggregateTotalMinutes = Object.values(statsObjectMemoized).reduce(
        (acc, current) => acc + current.total_minutes,
        0,
    );

    const aggregateTotalOccurrences = Object.values(statsObjectMemoized).reduce(
        (acc, current) => acc + current.total_occurrences,
        0,
    );

    const pieChartDataMemoized: IPieChartData[] = useMemo(() => {
        const pieChartData: IPieChartData[] = [];
        for (const key in statsObjectMemoized) {
            const item = {
                id: truncateLongText(statsObjectMemoized[key].name, 25),
                name: truncateLongText(statsObjectMemoized[key].name, 25),
                value:
                    queryFilters.displayIn === "minutes"
                        ? calculateGraphTwoDigitPercentage(
                              statsObjectMemoized[key].total_minutes,
                              aggregateTotalMinutes,
                          )
                        : calculateGraphTwoDigitPercentage(
                              statsObjectMemoized[key].total_occurrences,
                              aggregateTotalOccurrences,
                          ),
                color: statsObjectMemoized[key].color_code,
                total:
                    queryFilters.displayIn === "minutes"
                        ? statsObjectMemoized[key].total_minutes
                        : statsObjectMemoized[key].total_occurrences,
            };

            pieChartData.push(item);
        }

        return pieChartData;
    }, [statsObjectMemoized, queryFilters.displayIn, aggregateTotalMinutes, aggregateTotalOccurrences]);

    useEffect(() => {
        if (pieChartDataMemoized) {
            const nonStringValues = pieChartDataMemoized.map((item) =>
                Object.values(item).filter((data) => typeof data !== "string"),
            );

            setNoInitialDataInMinutes(
                Object.values(nonStringValues).every((item) => typeof item[0] === "number" && isNaN(item[0])),
            );
        }
    }, [pieChartDataMemoized]);

    return (
        <Box className={classes.container} sx={{ flexDirection: "column" }}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title} variant="subtitle1">
                    {t("locations.summary_report.overall_downtime_summary.title")}
                </Typography>
            </Box>
            {!pieChartDataMemoized.length || noInitialDataInMinutes ? (
                <Box className={classes.noData}>
                    <NoData />
                </Box>
            ) : (
                <>
                    <Box className={classes.graphContainer}>
                        <Box className={classes.graphGridItemContainer}>
                            <OverallDowntimeSummaryPieChart
                                data={pieChartDataMemoized}
                                pieChartCenteredMetric={OverallDowntimeSummaryPieChartCenteredMetric(highlight!)}
                                setHighlight={setHighlight}
                            />
                            <OverallDowntimeSummaryPieLegends data={pieChartDataMemoized} />
                        </Box>
                        <Box className={classes.totalDowntimeBox}>
                            <Typography variant="subtitle2" className={classes.totalDowntimeTitle}>
                                {t("locations.summary_report.overall_downtime_summary.total_downtime").toUpperCase()}:
                            </Typography>
                            <Typography variant="subtitle2" className={classes.totalDowntimeNumber}>
                                {queryFilters.displayIn === "minutes"
                                    ? `${aggregateTotalMinutes} ${t(
                                          "locations.summary_report.overall_downtime_summary.minutes_abbreviation",
                                      )}`
                                    : `${aggregateTotalOccurrences} ${t(
                                          "locations.summary_report.overall_downtime_summary.occurrences_abbreviation",
                                      )}`}
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};
